import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Avatar, AvatarGroup, Box, IconButton, Tooltip, styled } from '@mui/material';
import { memo } from 'react';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import Iconify from "../Iconify";
import {Platforms, PlatformsObj} from "../../helper/commonFunctions";
import pathRoute from "../../PathRoute";

const SidebarContainer = styled(Box)(({ theme, isOpen }) => ({
    width: isOpen ? 280 : 70,
    height: 'calc(100vh - 80px)',
    overflowY: 'auto',
    backgroundColor: '#fff',
    borderRight: '1px solid #eee',
    transition: 'width 0.3s ease',
    position: 'fixed',
    left: 0,
    top: 65,
    padding: '12px 0',
    zIndex: 999,
    '&:hover': {
        [theme.breakpoints.down('sm')]: {
            width: 280,
        },
    },
    [theme.breakpoints.up('lg')]: {
        top: 80,
    },
    [theme.breakpoints.down('sm')]: {
        width: isOpen ? 280 : 65,
        height: 'calc(100vh - 65px)',
    },
}));

const NavItem = styled(NavLink)(() => ({
    display: 'flex',
    alignItems: 'center',
    padding: '12px 24px',
    textDecoration: 'none',
    color: '#333',
    gap: '10px',
    height: 60,
    '&:hover': {
        backgroundColor: '#f5f5f5',
    },
    '&.active': {
        backgroundColor: '#e3f2fd',
        color: '#1976d2',
    },
}));

const SocialIcon = styled(Box)({
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const NavItemContent = memo(({ item, isOpen }) => {
    const isMobile = window.innerWidth <= 600;
    const showContent = isOpen || isMobile;

    return (
        <>
            <SocialIcon>
                <Iconify
                    icon={item.icon}
                    sx={{
                        width: 24,
                        height: 24,
                        color: item.connected ? PlatformsObj[item.name].color  : '#333',
                    }}
                />
            </SocialIcon>
            {showContent && <Box sx={{ flex: 1 }}>{item.name}</Box>}
            {showContent && (
                <Tooltip title={item.connected ? 'Connected' : 'Click to connect this network'} placement="right">
                    <IconButton size="small">
                        {item.connected ? (
                            <AvatarGroup
                                spacing={10}
                                total={item?.data?.pages?.length > 0 ? item?.data?.pages?.length : 1}
                                sx={{
                                    '& .MuiAvatar-root': { width: 30, height: 30 },
                                    '& .MuiAvatarGroup-avatar': { width: '30px', height: '30px', fontSize: '12px' },
                                }}
                            >
                                {item?.data?.pages?.length > 0 ? (
                                    <>
                                        {item?.data?.pages?.slice(0, 3).map((page) => (
                                            <Avatar
                                                key={page.page_id}
                                                alt={page.page_name}
                                                src={page.page_picture}
                                                sx={{ width: 30, height: 30 }}
                                            />
                                        ))}
                                    </>
                                ) : (
                                    <Avatar
                                        key={item?.data?.profile_id}
                                        alt={item?.data?.profile_name}
                                        src={item?.data?.profile_picture}
                                        sx={{ width: 30, height: 30 }}
                                    />
                                )}
                            </AvatarGroup>
                        ) : (''
                            // <AddCircleOutline sx={{ fontSize: 24 }} />
                        )}
                    </IconButton>
                </Tooltip>
            )}
        </>
    );
});

const AnalyticsSidebar = ({ isOpen, setIsOpen }) => {
    const currentBrand = useSelector((state) => state?.user?.data?.currentBrand);
    const profiles = currentBrand?.profiles || [];

    const platformIntegration = (name) =>
        profiles.find((platform) => String(platform.platform_name).toLowerCase().includes(name.toLowerCase()));

    // Social Links menus
    const socialLinks = [
        {
            name: Platforms.facebook,
            icon: PlatformsObj[Platforms.facebook].icon,
            path: pathRoute.analytics_pages(Platforms.facebook),
            connected: platformIntegration(Platforms.facebook),
            data: platformIntegration(Platforms.facebook),
        },
        {
            name: Platforms.linkedin,
            icon: PlatformsObj[Platforms.linkedin].icon,
            path: pathRoute.analytics_pages(Platforms.linkedin),
            connected: platformIntegration(Platforms.linkedin),
            data: platformIntegration(Platforms.linkedin),
        },
        {
            name: Platforms.twitter,
            icon: PlatformsObj[Platforms.twitter].icon,
            path: pathRoute.analytics_pages(Platforms.twitter),
            connected: platformIntegration(Platforms.twitter),
            data: platformIntegration(Platforms.twitter),
        },
    ];

    return (
        <SidebarContainer isOpen={isOpen}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {socialLinks.map((item) => (
                        <NavItem key={item.name} to={item.path} sx={{textTransform: 'capitalize'}}>
                            <NavItemContent item={item} isOpen={isOpen}/>
                        </NavItem>
                    ))}
                </Box>

                {/* Toggle Button */}
                <Box
                    sx={{
                        display: { xs: 'none', sm: 'flex' },
                        justifyContent: 'start',
                        mt: 2,
                        ml: 2,
                    }}
                >
                    <IconButton onClick={() => setIsOpen(!isOpen)}>
                        {isOpen ? <KeyboardArrowLeftIcon /> : <KeyboardArrowRightIcon />}
                    </IconButton>
                </Box>
            </Box>
        </SidebarContainer>
    );
};

export default memo(AnalyticsSidebar);
